<template>
    <v-container class="permissions-container">
        <!-- Header Section -->
        <v-card class="header-card mb-6" elevation="0">
            <v-row align="center" class="px-4 py-3">
                <v-col cols="12" sm="6">
                    <h1 class="text-h4 font-weight-bold header-title">ניהול הרשאות משתמשים</h1>
                    <span class="subtitle-1 grey--text">ניהול גישות למסדי נתונים וטבלאות</span>
                </v-col>
                <v-col cols="12" sm="6" class="d-flex justify-end align-center">
                    <v-btn class="save-btn ma-2" dark :color="$store.state.colorPrimary" @click="savePermissions"
                        :loading="saving" elevation="4">
                        <v-icon left>mdi-content-save</v-icon>
                        שמור שינויים
                    </v-btn>
                </v-col>
            </v-row>
        </v-card>

        <v-row>
            <!-- Left Panel - Permission Configuration -->
            <v-col cols="12" lg="8">
                <v-card class="content-card mb-4 modern-card">
                    <v-card-title class="card-header pb-2">
                        <v-icon left color="#0d2c6d">mdi-account-cog</v-icon>
                        <span class="card-title-text">הגדרת הרשאות</span>
                    </v-card-title>
                    <v-divider class="divider-gradient"></v-divider>

                    <v-card-text class="pt-4">
                        <!-- User Selection -->
                        <v-row>
                            <v-col cols="12" md="6">
                                <v-autocomplete v-model="selectedUser" :items="usersList" label="בחר משתמש" outlined
                                    dense :item-text="'name'" :item-value="'_id'" prepend-inner-icon="mdi-account"
                                    @change="onUserChange" clearable class="modern-select" :filter="customUserFilter">
                                    <template v-slot:item="{ item }">
                                        <v-list-item-content>
                                            <v-list-item-title>{{ item.name }}</v-list-item-title>
                                            <v-list-item-subtitle>{{ item.email }} - {{ getRoleText(item.role)
                                            }}</v-list-item-subtitle>
                                        </v-list-item-content>
                                    </template>
                                </v-autocomplete>
                            </v-col>

                            <!-- Database Selection -->
                            <v-col cols="12" md="6">
                                <!-- Updated to use workspacesList instead of databasesList -->
                                <v-autocomplete v-model="selectedDatabase" :items="workspacesList"
                                    label="בחר סביבת עבודה" outlined dense :item-text="'description'"
                                    :item-value="'_id'" prepend-inner-icon="mdi-database" @change="onDatabaseChange"
                                    :disabled="!selectedUser" clearable class="modern-select" :filter="customDatabaseFilter">
                                    <template v-slot:item="{ item }">
                                        <v-list-item-content>
                                            <v-list-item-title>{{ item.description }}</v-list-item-title>
                                            <v-list-item-subtitle>{{ item.dataName }} - {{ item.members.length }}
                                                חברים</v-list-item-subtitle>
                                        </v-list-item-content>
                                    </template>
                                </v-autocomplete>
                            </v-col>
                        </v-row>

                        <!-- Permission Configuration -->
                        <div v-if="selectedUser && selectedDatabase" class="mt-4">
                            <v-divider class="divider-gradient mb-4"></v-divider>

                            <!-- Access Type Selection -->
                            <v-row>
                                <v-col cols="12">
                                    <div class="access-type-section">
                                        <h3 class="section-title mb-3">
                                            <v-icon left color="#0d2c6d">mdi-shield-check</v-icon>
                                            סוג גישה
                                        </h3>
                                        <v-card outlined class="radio-card pa-4">
                                            <v-radio-group v-model="accessType" row @change="onAccessTypeChange"
                                                class="modern-radio-group">
                                                <v-radio label="גישה לכל הטבלאות" value="all" color="primary"
                                                    class="modern-radio"></v-radio>
                                                <v-radio label="גישה מוגבלת לטבלאות ספציפיות" value="limited"
                                                    color="primary" class="modern-radio"></v-radio>
                                            </v-radio-group>
                                        </v-card>
                                    </div>
                                </v-col>
                            </v-row>

                            <!-- Global Permissions (for all tables) -->
                            <div v-if="accessType === 'all'" class="mt-4">
                                <div class="permissions-section">
                                    <h3 class="section-title mb-3">
                                        <v-icon left color="#0d2c6d">mdi-key</v-icon>
                                        הרשאות כלליות לכל הטבלאות
                                    </h3>
                                    <v-card outlined class="permissions-card pa-4">
                                        <v-row dense class="permissions-grid">
                                            <!-- Made checkboxes more compact with better spacing -->
                                            <v-col cols="6" sm="4" v-for="permission in permissionTypes"
                                                :key="permission.value" class="permission-col">
                                                <v-checkbox v-model="globalPermissions" :value="permission.value"
                                                    :label="permission.text" :color="$store.state.colorPrimary"
                                                    hide-details dense class="modern-checkbox"></v-checkbox>
                                            </v-col>
                                        </v-row>
                                    </v-card>
                                </div>
                            </div>

                            <!-- Limited Table Access -->
                            <div v-if="accessType === 'limited'" class="mt-4">
                                <!-- Enhanced table selection section -->
                                <v-alert type="info" outlined dense class="modern-alert mb-3" icon="mdi-information">
                                    בחר תחילה את הטבלאות שברצונך לתת להן גישה, ולאחר מכן הגדר הרשאות לכל טבלה
                                </v-alert>

                                <v-card outlined class="table-selection-card mb-4" elevation="3">
                                    <v-card-title class="table-card-header pb-2">
                                        <v-icon left color="primary">mdi-table-multiple</v-icon>
                                        <span>בחירת טבלאות</span>
                                        <v-spacer></v-spacer>
                                        <v-chip v-if="selectedTables.length > 0" small color="success" outlined
                                            class="count-chip">
                                            {{ selectedTables.length }} טבלאות נבחרו
                                        </v-chip>
                                    </v-card-title>
                                    <v-divider class="divider-gradient"></v-divider>
                                    <v-card-text class="pt-4">
                                        <v-select v-model="selectedTables" :items="tablesList" label="בחר טבלאות להרשאה"
                                            outlined dense multiple chips deletable-chips :item-text="'hebrew_name'"
                                            :item-value="'name'" @change="onTablesChange" prepend-inner-icon="mdi-table"
                                            hint="בחר את הטבלאות שברצונך לתת להן גישה" persistent-hint
                                            :menu-props="{ maxHeight: '300px' }" class="modern-select">
                                            <template v-slot:selection="{ item, index }">
                                                <v-chip v-if="index < 3" :color="$store.state.colorPrimary"
                                                    text-color="white" small close @click:close="removeTable(item.name)"
                                                    class="selection-chip">
                                                    {{ item.hebrew_name }}
                                                </v-chip>
                                                <span v-if="index === 3" class="grey--text text-caption">
                                                    (+{{ selectedTables.length - 3 }} נוספות)
                                                </span>
                                            </template>
                                        </v-select>

                                        <!-- Selected Tables Summary -->
                                        <div v-if="selectedTables.length > 0" class="mt-3">
                                            <v-chip-group column class="tables-chip-group">
                                                <v-chip v-for="table in selectedTables" :key="table"
                                                    :color="getPermissionChipColor(table)" outlined small
                                                    @click="scrollToTable(table)" class="table-chip ma-1">
                                                    <v-icon left small>mdi-table</v-icon>
                                                    {{ getTableHebrewName(table) }}
                                                    <v-badge :content="getTablePermissionsCount(table)"
                                                        :color="getPermissionChipColor(table)" inline></v-badge>
                                                </v-chip>
                                            </v-chip-group>
                                        </div>
                                    </v-card-text>
                                </v-card>

                                <!-- Permissions per Table -->
                                <div v-if="selectedTables.length > 0" class="mt-4">
                                    <div class="permissions-section">
                                        <h3 class="section-title mb-3">
                                            <v-icon left color="#0d2c6d">mdi-key-variant</v-icon>
                                            הרשאות לכל טבלה
                                        </h3>
                                        <v-expansion-panels v-model="expandedPanels" multiple
                                            class="modern-expansion-panels">
                                            <v-expansion-panel v-for="table in selectedTables" :key="table"
                                                :ref="'table-' + table" class="modern-expansion-panel">
                                                <v-expansion-panel-header class="expansion-header">
                                                    <div class="d-flex align-center">
                                                        <v-icon left color="primary">mdi-table</v-icon>
                                                        <span class="font-weight-medium">{{ getTableHebrewName(table)
                                                        }}</span>
                                                        <v-spacer></v-spacer>
                                                        <v-chip small :color="getPermissionChipColor(table)" outlined
                                                            class="permissions-count-chip">
                                                            {{ getTablePermissionsCount(table) }} הרשאות
                                                        </v-chip>
                                                    </div>
                                                </v-expansion-panel-header>
                                                <v-expansion-panel-content>
                                                    <!-- Made table permissions checkboxes more compact and organized -->
                                                    <v-card outlined class="permissions-card pa-3">
                                                        <v-row dense class="permissions-grid">
                                                            <v-col cols="6" sm="4" v-for="permission in permissionTypes"
                                                                :key="permission.value" class="permission-col">
                                                                <v-checkbox v-model="tablePermissions[table]"
                                                                    :value="permission.value" :label="permission.text"
                                                                    :color="$store.state.colorPrimary" hide-details
                                                                    dense class="modern-checkbox"></v-checkbox>
                                                            </v-col>
                                                        </v-row>
                                                    </v-card>
                                                </v-expansion-panel-content>
                                            </v-expansion-panel>
                                        </v-expansion-panels>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>

            <!-- Right Panel - Current Permissions Overview -->
            <v-col cols="12" lg="4">
                <v-card class="content-card">
                    <v-card-title class="pb-2">
                        <v-icon left color="#0d2c6d">mdi-eye</v-icon>
                        <span style="color: #0d2c6d;">הרשאות קיימות</span>
                    </v-card-title>
                    <v-divider></v-divider>

                    <v-card-text v-if="selectedUser">
                        <div v-if="userPermissions.length === 0" class="text-center py-8">
                            <v-icon size="48" color="grey lighten-1">mdi-shield-off-outline</v-icon>
                            <p class="mt-2 grey--text">אין הרשאות מוגדרות למשתמש זה</p>
                        </div>

                        <!-- Enhanced permissions display with better styling and organization -->
                        <div v-else>
                            <v-expansion-panels v-model="permissionsPanels" multiple accordion>
                                <v-expansion-panel v-for="(perm, index) in userPermissions" :key="index"
                                    class="permission-panel">
                                    <v-expansion-panel-header class="permission-header">
                                        <div class="d-flex align-center w-100">
                                            <v-icon left color="primary">mdi-database</v-icon>
                                            <div class="flex-grow-1">
                                                <div class="font-weight-bold">
                                                    {{ perm.database }}
                                                    <!-- Added role display next to workspace name -->
                                                    <v-chip small :color="getRoleColor(perm.role)" text-color="white"
                                                        class="mr-2">
                                                        {{ getRoleText(perm.role) }}
                                                    </v-chip>
                                                </div>
                                                <div class="caption grey--text">
                                                    {{ perm.permissions.length }} הרשאות
                                                    <span v-if="perm.hasTableLimit && perm.tablePermissions.length > 0">
                                                        • {{ perm.tablePermissions.length }} טבלאות
                                                    </span>
                                                </div>
                                            </div>
                                            <v-btn icon small @click.stop="removeUserPermission(index)" class="ml-2">
                                                <v-icon small color="error">mdi-delete</v-icon>
                                            </v-btn>
                                        </div>
                                    </v-expansion-panel-header>

                                    <v-expansion-panel-content>
                                        <div class="permissions-content">
                                            <!-- General Permissions -->
                                            <div class="mb-3">
                                                <h4 class="subtitle-2 mb-2" style="color: #0d2c6d;">הרשאות כלליות:</h4>
                                                <v-chip-group column>
                                                    <v-chip v-for="permission in perm.permissions" :key="permission"
                                                        small :color="$store.state.colorPrimary" text-color="white">
                                                        <v-icon left x-small>mdi-key</v-icon>
                                                        {{ getPermissionText(permission) }}
                                                    </v-chip>
                                                </v-chip-group>
                                            </div>

                                            <!-- Enhanced table permissions display with specific permissions for each table -->
                                            <div v-if="perm.hasTableLimit && perm.tablePermissions.length > 0">
                                                <v-divider class="mb-3"></v-divider>
                                                <h4 class="subtitle-2 mb-2" style="color: #0d2c6d;">הרשאות לטבלאות
                                                    ספציפיות:</h4>

                                                <v-expansion-panels accordion class="table-permissions-panels">
                                                    <v-expansion-panel
                                                        v-for="(tablePermission, tableIndex) in perm.tablePermissions"
                                                        :key="tableIndex" class="table-permission-panel">
                                                        <v-expansion-panel-header class="table-permission-header">
                                                            <div class="d-flex align-center w-100">
                                                                <v-icon left small color="success">mdi-table</v-icon>
                                                                <span class="font-weight-medium">{{
                                                                    tablePermission.tableName }}</span>
                                                                <v-spacer></v-spacer>
                                                                <v-chip x-small
                                                                    :color="tablePermission.permissions.length > 0 ? 'success' : 'grey'"
                                                                    text-color="white" class="permissions-count">
                                                                    {{ tablePermission.permissions.length }} הרשאות
                                                                </v-chip>
                                                            </div>
                                                        </v-expansion-panel-header>

                                                        <v-expansion-panel-content>
                                                            <div class="table-permissions-content pt-2">
                                                                <v-chip-group column>
                                                                    <v-chip
                                                                        v-for="permission in tablePermission.permissions"
                                                                        :key="permission" x-small color="success"
                                                                        text-color="white"
                                                                        class="table-permission-chip">
                                                                        <v-icon left x-small>mdi-key-variant</v-icon>
                                                                        {{ getPermissionText(permission) }}
                                                                    </v-chip>
                                                                </v-chip-group>

                                                                <div v-if="tablePermission.permissions.length === 0"
                                                                    class="text-center py-2">
                                                                    <span class="caption grey--text">אין הרשאות מוגדרות
                                                                        לטבלה
                                                                        זו</span>
                                                                </div>
                                                            </div>
                                                        </v-expansion-panel-content>
                                                    </v-expansion-panel>
                                                </v-expansion-panels>
                                            </div>
                                        </div>
                                    </v-expansion-panel-content>
                                </v-expansion-panel>
                            </v-expansion-panels>
                        </div>
                    </v-card-text>

                    <v-card-text v-else class="text-center py-8">
                        <v-icon size="48" color="grey lighten-1">mdi-account-search</v-icon>
                        <p class="mt-2 grey--text">בחר משתמש לצפייה בהרשאות</p>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <!-- Snackbar -->
        <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="4000" bottom right>
            {{ snackbarText }}
            <template v-slot:action="{ attrs }">
                <v-btn text v-bind="attrs" @click="snackbar = false">
                    סגור
                </v-btn>
            </template>
        </v-snackbar>

        <!-- Loading Overlay -->
        <v-overlay :value="loading">
            <v-progress-circular indeterminate size="64" :color="$store.state.colorPrimary"></v-progress-circular>
        </v-overlay>
    </v-container>
</template>

<script>
import ApiServices from '@/services/api.service'
import Auth from '@/services/auth.service'
import { get } from 'lodash';

export default {
    name: 'PermissionsComp',
    data() {
        return {
            loading: false,
            saving: false,
            snackbar: false,
            snackbarColor: 'success',
            snackbarText: '',
            // User and Workspace Selection
            selectedUser: null,
            selectedDatabase: null,
            usersList: [],
            workspacesList: [],
            // Permission Configuration
            accessType: 'all',
            globalPermissions: [],
            selectedTables: [],
            tablePermissions: {},
            expandedPanels: [],
            // Tables List (mock data - should come from API)
            tablesList: [
                { name: 'users', hebrew_name: 'משתמשים' },
                { name: 'products', hebrew_name: 'מוצרים' },
                { name: 'orders', hebrew_name: 'הזמנות' },
                { name: 'customers', hebrew_name: 'לקוחות' },
                { name: 'inventory', hebrew_name: 'מלאי' },
                { name: 'reports', hebrew_name: 'דוחות' },
                { name: 'settings', hebrew_name: 'הגדרות' },
                { name: 'logs', hebrew_name: 'לוגים' }
            ],
            // Permission Types
            permissionTypes: [
                { text: 'קריאה', value: 'read' },
                { text: 'כתיבה', value: 'write' },
                { text: 'מחיקה', value: 'delete' },
                { text: 'תזמון משימות', value: 'scheduler' },
                { text: 'אינטיגרציות', value: 'webhook' },
                { text: 'עריכת שדות', value: 'fields' }
            ],
            // Current User Permissions
            userPermissions: [],
            permissionsPanels: [0], // Default to first panel open
            isAutoLoading: false,
        }
    },
    methods: {
        async getUsersList() {
            try {
                this.loading = true;
                let token = localStorage.getItem("token");
                let api = process.env.VUE_APP_BASE_URL + "/admin/get_users_permissions_list";
                const res = await fetch(api, ApiServices.requestOptions("GET", "", token));
                const jsonObject = await res.json();

                if (res.status === 200) {
                    this.usersList = jsonObject.filter(user => user.role === 'user' || user.role === 'agent');
                } else {
                    this.showSnackBar("שגיאה בטעינת רשימת המשתמשים", "error");
                }
            } catch (error) {
                this.showSnackBar("שגיאה בטעינת נתונים: " + error, "error");
            } finally {
                this.loading = false;
            }
        },
        async getWorkspacesList() {
            try {
                this.loading = true;
                let token = localStorage.getItem("token");
                let api = process.env.VUE_APP_BASE_URL + "/admin/get_workspaces_list";
                const res = await fetch(api, ApiServices.requestOptions("GET", "", token));
                const jsonObject = await res.json();

                if (res.status === 200) {
                    this.workspacesList = jsonObject;
                } else {
                    this.showSnackBar("שגיאה בטעינת רשימת סביבות העבודה", "error");
                }
            } catch (error) {
                this.showSnackBar("שגיאה בטעינת נתונים: " + error, "error");
            } finally {
                this.loading = false;
            }
        },
        async getTablesNames() {
            console.log(555555);

            let api = process.env.VUE_APP_BASE_URL + "/admin/get_workspace_tables?workspaceId=" + this.selectedDatabase;
            let token = localStorage.getItem("token");
            try {
                const response = await fetch(api, ApiServices.requestOptions("GET", "", token));
                if (response.status >= 400) {
                    return "error";
                } else if (response.status === 200) {
                    console.log(66666);

                    const jsonObject = await response.json();
                    this.tablesList = jsonObject;
                }
            } catch (error) {
                console.log(error);
                return "error";
            }
        },
        async loadUserPermissions(userId) {
            try {
                const selectedUserData = this.usersList.find(user => user._id === userId);
                if (selectedUserData && selectedUserData.workspaces) {
                    this.userPermissions = selectedUserData.workspaces.map(workspace => ({
                        database: workspace.description,
                        workspaceId: workspace._id,
                        role: workspace.member.role,
                        permissions: workspace.member.defaultPermissions || [],
                        hasTableLimit: workspace.member.hasTableLimit,
                        tables: workspace.member.tablePermissions || [],
                        tablePermissions: workspace.member.tablePermissions || []
                    }));
                } else {
                    this.userPermissions = [];
                }
            } catch (error) {
                this.showSnackBar("שגיאה בטעינת הרשאות המשתמש", "error");
            }
        },
        async savePermissions() {
            if (!this.selectedUser || !this.selectedDatabase) {
                this.showSnackBar("יש לבחור משתמש וסביבת עבודה", "warning");
                return;
            }

            try {
                this.saving = true;

                const permissionData = JSON.stringify({
                    userId: this.selectedUser,
                    workspaceId: this.selectedDatabase,
                    accessType: this.accessType,
                    permissions: this.accessType === 'all' ? {_default: this.globalPermissions} : this.tablePermissions,
                    tables: this.accessType === 'limited' ? this.selectedTables : null,
                    hasTableLimit: this.accessType === 'limited'
                });

                let token = localStorage.getItem("token");
                let api = process.env.VUE_APP_BASE_URL + "/admin/update_user_permissions";
                const res = await fetch(api, ApiServices.requestOptions("PUT", permissionData, token));
                const jsonObject = await res.json();

                if (res.status === 200) {
                    this.showSnackBar("ההרשאות נשמרו בהצלחה", "success");
                    this.loadUserPermissions(this.selectedUser);
                } else {
                    this.showSnackBar("שגיאה בשמירת ההרשאות", "error");
                }
            } catch (error) {
                this.showSnackBar("שגיאה בשמירת ההרשאות: " + error, "error");
            } finally {
                this.saving = false;
            }
        },
        onUserChange() {
            this.resetPermissionForm();
            if (this.selectedUser) {
                this.loadUserPermissions(this.selectedUser);
            }
        },
        async onDatabaseChange() {
            this.resetPermissionSettings();
            if (this.selectedUser && this.selectedDatabase) {
                await this.getTablesNames();
                this.isAutoLoading = true;
                await this.loadExistingPermissionsForWorkspace();
                console.log(22222);
                console.log(this.selectedDatabase);
                this.isAutoLoading = false;
            }
        },
        onAccessTypeChange() {
            if (!this.isAutoLoading) {
                this.globalPermissions = [];
                this.selectedTables = [];
                this.tablePermissions = {};
                this.expandedPanels = [];
            }
        },
        onTablesChange() {
            // Initialize permissions for newly selected tables
            this.selectedTables.forEach(table => {
                if (!this.tablePermissions[table]) {
                    this.$set(this.tablePermissions, table, []);
                }
            });

            // Remove permissions for unselected tables
            Object.keys(this.tablePermissions).forEach(table => {
                if (!this.selectedTables.includes(table)) {
                    this.$delete(this.tablePermissions, table);
                }
            });
        },
        resetPermissionForm() {
            this.selectedDatabase = null;
            this.resetPermissionSettings();
        },
        resetPermissionSettings() {
            this.accessType = 'all';
            this.globalPermissions = [];
            this.selectedTables = [];
            this.tablePermissions = {};
            this.expandedPanels = [];
        },
        async removeUserPermission(index) {
            const permission = this.userPermissions[index];

            try {
                const deleteData = JSON.stringify({
                    userId: this.selectedUser,
                    workspaceId: permission.workspaceId
                });

                let token = localStorage.getItem("token");
                let api = process.env.VUE_APP_BASE_URL + "/admin/delete_user_permission";
                const res = await fetch(api, ApiServices.requestOptions("DELETE", deleteData, token));

                if (res.status === 200) {
                    // Only remove from local array if API call succeeded
                    this.userPermissions.splice(index, 1);
                    this.showSnackBar("ההרשאה נמחקה בהצלחה", "success");
                } else {
                    this.showSnackBar("שגיאה במחיקת ההרשאה", "error");
                }
            } catch (error) {
                this.showSnackBar("שגיאה במחיקת ההרשאה: " + error, "error");
            }
        },
        removeTable(tableName) {
            const index = this.selectedTables.indexOf(tableName);
            if (index > -1) {
                this.selectedTables.splice(index, 1);
                this.onTablesChange();
            }
        },
        scrollToTable(tableName) {
            const tableIndex = this.selectedTables.indexOf(tableName);
            if (tableIndex > -1 && !this.expandedPanels.includes(tableIndex)) {
                this.expandedPanels.push(tableIndex);
            }
            this.$nextTick(() => {
                const element = this.$refs['table-' + tableName];
                if (element && element[0]) {
                    element[0].$el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            });
        },
        getRoleText(role) {
            switch (role) {
                case 'superAdmin': return 'מנהל ראשי';
                case 'admin': return 'מנהל';
                case 'agent': return 'סוכן';
                case 'owner': return 'בעלים';
                case 'member': return 'חבר';
                default: return 'משתמש';
            }
        },
        getRoleColor(role) {
            switch (role) {
                case 'superAdmin': return 'red darken-2';
                case 'admin': return 'orange darken-2';
                case 'agent': return 'blue darken-1';
                case 'owner': return 'purple darken-2';
                default: return 'grey darken-1';
            }
        },
        getTableHebrewName(tableName) {
            const table = this.tablesList.find(t => t.name === tableName);
            return table ? table.hebrew_name : tableName;
        },
        getTablePermissionsCount(tableName) {
            return this.tablePermissions[tableName] ? this.tablePermissions[tableName].length : 0;
        },
        getPermissionChipColor(tableName) {
            const count = this.getTablePermissionsCount(tableName);
            if (count === 0) return 'grey';
            if (count <= 2) return 'warning';
            return 'success';
        },
        getPermissionText(permission) {
            const perm = this.permissionTypes.find(p => p.value === permission);
            return perm ? perm.text : permission;
        },
        showSnackBar(message, color) {
            this.snackbarText = message;
            this.snackbarColor = color;
            this.snackbar = true;
        },
        async loadExistingPermissionsForWorkspace() {
            try {
                // Find existing permissions for the selected user and workspace
                const existingPermission = this.userPermissions.find(perm =>
                    perm.workspaceId === this.selectedDatabase
                );

                if (existingPermission) {
                    // Set access type based on whether there are table limitations
                    this.accessType = existingPermission.hasTableLimit ? 'limited' : 'all';

                    if (existingPermission.hasTableLimit) {
                        // Load table-specific permissions
                        this.selectedTables = existingPermission.tablePermissions.map(tp => tp.tableName);

                        // Initialize table permissions object
                        this.tablePermissions = {};
                        existingPermission.tablePermissions.forEach(tp => {
                            this.$set(this.tablePermissions, tp.tableName, tp.permissions || []);
                        });

                        // Expand all panels to show loaded permissions
                        this.expandedPanels = this.selectedTables.map((_, index) => index);
                    } else {
                        // Load global permissions
                        this.globalPermissions = [...(existingPermission.permissions || [])];
                    }

                    this.showSnackBar("הרשאות קיימות נטענו בהצלחה", "info");
                } else {
                    // No existing permissions found - keep form empty
                    this.showSnackBar("לא נמצאו הרשאות קיימות עבור סביבת עבודה זו", "info");
                }
            } catch (error) {
                this.showSnackBar("שגיאה בטעינת הרשאות קיימות: " + error, "error");
            }
        },
        customDatabaseFilter(item, queryText, itemText) {
            const text = (itemText || '').toString().toLowerCase()
            const dataName = (item.dataName || '').toString().toLowerCase()
            const query = queryText.toString().toLowerCase()

            return text.indexOf(query) > -1 || dataName.indexOf(query) > -1
        },
        customUserFilter(item, queryText, itemText) {
            const text = (itemText || '').toString().toLowerCase()
            const dataName = (item.email || '').toString().toLowerCase()
            const query = queryText.toString().toLowerCase()

            return text.indexOf(query) > -1 || dataName.indexOf(query) > -1
        }
    },
    mounted() {
        this.getUsersList();
        this.getWorkspacesList();
    }
}
</script>

<style scoped>
.permissions-container {
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
    min-height: 100vh;
    padding-top: 2rem;
    margin-top: 60px;
}

.header-card {
    background: linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%);
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.header-title {
    color: #0d2c6d;
    text-shadow: 0 2px 4px rgba(13, 44, 109, 0.1);
}

.save-btn {
    border-radius: 12px;
    text-transform: none;
    font-weight: 600;
    transition: all 0.3s ease;
}

.save-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.modern-card {
    background: linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%);
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
    overflow: hidden;
}

.card-header {
    background: linear-gradient(135deg, #f0f4ff 0%, #e8f2ff 100%);
    border-bottom: 1px solid #e3f2fd;
}

.card-title-text {
    color: #0d2c6d;
    font-weight: 600;
}

.divider-gradient {
    background: linear-gradient(90deg, transparent 0%, #e3f2fd 50%, transparent 100%);
    height: 2px;
    border: none;
}

.section-title {
    color: #0d2c6d;
    font-weight: 600;
    font-size: 1.1rem;
}

.access-type-section,
.permissions-section {
    animation: slideInUp 0.5s ease-out;
}

.radio-card {
    background: linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%);
    border: 2px solid #e3f2fd;
    border-radius: 12px;
    transition: all 0.3s ease;
}

.radio-card:hover {
    border-color: #2196f3;
    box-shadow: 0 4px 20px rgba(33, 150, 243, 0.1);
}

.modern-radio-group {
    margin: 0;
}

.modern-radio {
    margin-left: 24px;
}

.permissions-card {
    background: linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%);
    border: 2px solid #e8f5e8;
    border-radius: 12px;
    transition: all 0.3s ease;
}

.permissions-card:hover {
    border-color: #4caf50;
    box-shadow: 0 4px 20px rgba(76, 175, 80, 0.1);
}

.permissions-grid {
    gap: 8px;
}

.permission-col {
    padding: 4px 8px;
}

.modern-checkbox {
    margin-bottom: 0;
}

.modern-checkbox>>>.v-input__control {
    min-height: 28px;
}

.modern-checkbox>>>.v-label {
    font-size: 14px;
    line-height: 1.3;
    color: #424242;
    font-weight: 500;
}

.modern-alert {
    background: linear-gradient(135deg, #e3f2fd 0%, #f0f8ff 100%);
    border: 1px solid #2196f3;
    border-radius: 12px;
}

.table-selection-card {
    border: 2px solid #e3f2fd;
    border-radius: 16px;
    background: linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%);
    transition: all 0.3s ease;
}

.table-selection-card:hover {
    border-color: #2196f3;
    box-shadow: 0 8px 32px rgba(33, 150, 243, 0.15);
}

.table-card-header {
    background: linear-gradient(135deg, #f0f4ff 0%, #e8f2ff 100%);
    border-bottom: 1px solid #e3f2fd;
}

.count-chip {
    animation: pulse 2s infinite;
}

.modern-select>>>.v-input__control {
    border-radius: 12px;
}

.modern-select>>>.v-text-field__details {
    margin-top: 4px;
}

.selection-chip {
    margin: 2px;
    transition: all 0.3s ease;
}

.selection-chip:hover {
    transform: scale(1.05);
}

.tables-chip-group {
    animation: fadeInUp 0.5s ease-out;
}

.table-chip {
    transition: all 0.3s ease;
    cursor: pointer;
}

.table-chip:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.modern-expansion-panels {
    border-radius: 12px;
    overflow: hidden;
}

.modern-expansion-panel {
    margin-bottom: 12px;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;
}

.modern-expansion-panel:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.expansion-header {
    background: linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%);
    border-bottom: 1px solid #e3f2fd;
}

.permissions-count-chip {
    animation: fadeIn 0.3s ease-in-out;
}

.table-permissions-panels {
    margin-top: 8px;
}

.table-permission-panel {
    margin-bottom: 8px;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.table-permission-header {
    background: linear-gradient(135deg, #f0f8f0 0%, #ffffff 100%);
    border-bottom: 1px solid #e8f5e8;
    min-height: 40px !important;
}

.table-permissions-content {
    background: #fafafa;
}

.table-permission-chip {
    margin: 2px;
    transition: all 0.2s ease;
}

.table-permission-chip:hover {
    transform: scale(1.05);
}

.permissions-count {
    animation: fadeIn 0.3s ease-in-out;
}

/* RTL Specific Styles */
.v-application {
    direction: rtl;
}

/* Animations */
@keyframes slideInUp {
    from {
        opacity: 0;
        transform: translateY(30px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

@keyframes pulse {
    0% {
        transform: scale(1);
    }

    50% {
        transform: scale(1.05);
    }

    100% {
        transform: scale(1);
    }
}

/* Custom Scrollbar */
::-webkit-scrollbar {
    width: 8px;
    height: 8px;
}

::-webkit-scrollbar-track {
    background: linear-gradient(135deg, #f1f1f1 0%, #e8e8e8 100%);
    border-radius: 4px;
}

::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #888 0%, #666 100%);
    border-radius: 4px;
    transition: all 0.3s ease;
}

::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #666 0%, #444 100%);
}

/* Responsive Design */
@media (max-width: 960px) {
    .permission-col {
        padding: 2px 4px;
    }

    .modern-checkbox>>>.v-input__control {
        min-height: 24px;
    }

    .table-permission-header {
        min-height: 36px !important;
    }

    .table-permission-chip {
        font-size: 11px !important;
    }
}

@media (max-width: 600px) {
    .permissions-container {
        padding-top: 1rem;
    }

    .header-card {
        margin-bottom: 1rem !important;
    }

    .permission-col {
        padding: 1px 2px;
    }

    .modern-checkbox>>>.v-input__control {
        min-height: 24px;
    }
}
</style>
